// frontend/src/hooks/useCreatorConnect.ts
//
// Hook for managing Stripe Connect onboarding and dashboard access.

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UseCreatorConnectReturn {
  loading: boolean;
  error: string | null;
  // Start Stripe Connect onboarding flow
  startOnboarding: () => Promise<{ alreadyOnboarded?: boolean } | null>;
  // Open Stripe Express dashboard
  openDashboard: () => Promise<void>;
  // Clear error state
  clearError: () => void;
}

export function useCreatorConnect(): UseCreatorConnectReturn {
  const { session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startOnboarding = useCallback(async () => {
    if (!session?.access_token) {
      setError('Please sign in to connect your Stripe account');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/connect-onboarding`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start onboarding');
      }

      if (data.alreadyOnboarded) {
        return { alreadyOnboarded: true };
      }

      if (data.url) {
        // Redirect to Stripe onboarding
        window.location.href = data.url;
      }

      return null;
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start onboarding');
      return null;
    } finally {
      setLoading(false);
    }
  }, [session]);

  const openDashboard = useCallback(async () => {
    if (!session?.access_token) {
      setError('Please sign in to access your dashboard');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/connect-dashboard`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (data.needsOnboarding) {
          setError('Please complete Stripe onboarding first');
          return;
        }
        throw new Error(data.error || 'Failed to open dashboard');
      }

      if (data.url) {
        // Open Stripe dashboard in new tab
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Dashboard error:', err);
      setError(err instanceof Error ? err.message : 'Failed to open dashboard');
    } finally {
      setLoading(false);
    }
  }, [session]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    startOnboarding,
    openDashboard,
    clearError,
  };
}